import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateGroup } from "../../../services/groupServices";
import { getAllEnseignants } from "../../../services/adherentServices";
import { toast } from "react-toastify";
import { getDojos } from "../../../services/dojoServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const colors = [
  { label: "Bleu", value: "bg-blue-400" },
  { label: "Vert", value: "bg-green-400" },
  { label: "Jaune", value: "bg-yellow-400" },
  { label: "Rouge", value: "bg-red-400" },
  { label: "Indigo", value: "bg-indigo-400" },
  { label: "Violet", value: "bg-purple-400" },
  { label: "Rose", value: "bg-pink-400" },
  { label: "Orange", value: "bg-orange-400" },
  { label: "Cyan", value: "bg-teal-400" },
  { label: "Marron", value: "bg-amber-600" },
  { label: "Turquoise", value: "bg-cyan-500" },
  { label: "Gris", value: "bg-gray-500" },
  { label: "Lime", value: "bg-lime-400" },
  { label: "Fuchsia", value: "bg-fuchsia-500" },
];

const EditGroupModal = ({ isActive, onClose, groupData, refreshGroups }) => {
  const [formData, setFormData] = useState({
    id: groupData.id,
    nom: groupData.nom,
    horaire1: groupData.horaire1,
    horaire2: groupData.horaire2,
    horaire3: groupData.horaire3 || "",
    horaire4: groupData.horaire4 || "",
    horaire4: groupData.horaire5 || "",
    horaire5: groupData.horaire6 || "",
    annees_naissance: groupData.annees_naissance,
    tarif_licence: groupData.tarif_licence,
    tarif_cotisation: groupData.tarif_cotisation,
    info_groupe: groupData.info_groupe || "",
    is_active: groupData.is_active,
    ordre_groupe: groupData.ordre_groupe,
    quota_groupe: groupData.quota_groupe,
    enseignant1: groupData.enseignant1 || "",
    enseignant2: groupData.enseignant2 || "",
    enseignant3: groupData.enseignant3 || "",
    enseignant4: groupData.enseignant4 || "",
    enseignant5: groupData.enseignant5 || "",
    enseignant6: groupData.enseignant6 || "",
    color_groupe: groupData.color_groupe || colors[0].value,
    dojo_id: groupData.dojo_id,
  });

  const [enseignants, setEnseignants] = useState([]);
  const [enseignantsList, setEnseignantsList] = useState([]);
  const [infoGroupe, setInfoGroupe] = useState("");
  const [modeAge, setModeAge] = useState("annees");
  const [anneesSelectionnees, setAnneesSelectionnees] = useState([]);
  const [ageMin, setAgeMin] = useState("");
  const [dojos, setDojos] = useState([]);

  const [horaires, setHoraires] = useState([{ jour: "", debut: "", fin: "" }]);

  useEffect(() => {
    const fetchDojos = async () => {
      try {
        const data = await getDojos();
        setDojos(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des dojos", error);
        toast.error("Erreur lors de la récupération des dojos");
      }
    };

    fetchDojos();
  }, []);

  const addHoraire = () => {
    if (horaires.length < 6) {
      setHoraires([...horaires, { jour: "", debut: "", fin: "" }]);
    }
  };

  const handleHoraireChange = (index, field, value) => {
    const newHoraires = [...horaires];
    newHoraires[index][field] = value;
    setHoraires(newHoraires);
  };

  const addEnseignant = () => {
    if (enseignantsList.length < 6) {
      setEnseignantsList([...enseignantsList, ""]);
    }
  };

  const handleEnseignantChange = (index, value) => {
    const newList = [...enseignantsList];
    newList[index] = value;
    setEnseignantsList(newList);
  };

  const currentYear = new Date().getFullYear();
  const listeAnnees = Array.from({ length: 20 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetchEnseignants = async () => {
      try {
        const data = await getAllEnseignants();
        setEnseignants(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des enseignants", error);
        toast.error("Erreur lors de la récupération des enseignants");
      }
    };

    fetchEnseignants();
  }, []);

  useEffect(() => {
    // reconstruction des horaires si déjà présents
    const extractHoraires = [];
    for (let i = 1; i <= 6; i++) {
      const value = groupData[`horaire${i}`];
      if (value) {
        const match = value.match(/(\w+)\s+(\d{2}:\d{2})-(\d{2}:\d{2})/);
        if (match) {
          extractHoraires.push({
            jour: match[1],
            debut: match[2],
            fin: match[3],
          });
        }
      }
    }

    const enseignantsInit = [];
    for (let i = 1; i <= 6; i++) {
      const e = groupData[`enseignant${i}`];
      if (e) enseignantsInit.push(e);
    }

    // mode années ou âge
    if (groupData.annees_naissance?.startsWith("+")) {
      setModeAge("ageMin");
      setAgeMin(
        groupData.annees_naissance.replace("+", "").replace(" ans", "")
      );
    } else {
      setModeAge("annees");
      setAnneesSelectionnees(
        groupData.annees_naissance?.split("-").map((y) => parseInt(y)) || []
      );
    }

    setFormData({
      id: groupData.id,
      nom: groupData.nom,
      tarif_licence: groupData.tarif_licence,
      tarif_cotisation: groupData.tarif_cotisation,
      is_active: groupData.is_active,
      ordre_groupe: groupData.ordre_groupe,
      quota_groupe: groupData.quota_groupe,
      color_groupe: groupData.color_groupe || colors[0].value,
      dojo_id: groupData.dojo_id,
    });
    setHoraires(extractHoraires);
    setEnseignantsList(enseignantsInit);
    setInfoGroupe(groupData.info_groupe || "");
  }, [groupData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleColorChange = (color) => {
    setFormData((prev) => ({
      ...prev,
      color_groupe: color.value, // Met à jour la couleur sélectionnée
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const horairesFormatted = horaires
        .filter((h) => h.jour && h.debut && h.fin)
        .map((h) => `${h.jour} ${h.debut}-${h.fin}`);

      let finalAnneesNaissance = "";
      if (modeAge === "annees") {
        finalAnneesNaissance = anneesSelectionnees
          .sort((a, b) => b - a)
          .join("-");
      } else if (modeAge === "ageMin" && ageMin) {
        finalAnneesNaissance = `+${ageMin} ans`;
      }

      const fullFormData = {
        ...formData,
        info_groupe: infoGroupe,
        enseignant1: enseignantsList[0] || "",
        enseignant2: enseignantsList[1] || "",
        enseignant3: enseignantsList[2] || "",
        enseignant4: enseignantsList[3] || "",
        enseignant5: enseignantsList[4] || "",
        enseignant6: enseignantsList[5] || "",
        horaire1: horairesFormatted[0] || "",
        horaire2: horairesFormatted[1] || "",
        horaire3: horairesFormatted[2] || "",
        horaire4: horairesFormatted[3] || "",
        horaire5: horairesFormatted[4] || "",
        horaire6: horairesFormatted[5] || "",
        annees_naissance: finalAnneesNaissance,
      };

      await updateGroup(fullFormData);
      onClose();
      refreshGroups();
      toast.success("Groupe mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du groupe", error);
      toast.error("Erreur lors de la mise à jour du groupe");
    }
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="modal-box bg-white dark:bg-slate-400 p-4 rounded-lg shadow-lg max-w-lg w-full">
        <h3 className="text-xl font-bold uppercase p-3 mb-4 text-center bg-primary text-white rounded-md">
          Editer un groupe
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4 flex flex-col">
          {/* Sélectionner un Dojo pour le groupe */}
          <label className="label">Dojo</label>
          <select
            name="dojo_id"
            value={formData.dojo_id}
            onChange={handleChange}
            className="input input-bordered w-full"
          >
            <option value="">Sélectionner un Dojo</option>
            {dojos.map((dojo) => (
              <option key={dojo.id} value={dojo.id}>
                {dojo.dojo_nom}
              </option>
            ))}
          </select>
          {/* Formulaire pour la mise à jour du groupe */}
          <input
            type="text"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            className="input input-bordered w-full"
            required
            placeholder="Nom du groupe"
          />

          {/* 🕓 Horaires dynamiques */}
          <label className="font-semibold">Horaires</label>
          {horaires.map((horaire, index) => (
            <div key={index} className="flex gap-2 items-center">
              <select
                value={horaire.jour}
                onChange={(e) =>
                  handleHoraireChange(index, "jour", e.target.value)
                }
                className="input input-bordered"
              >
                <option value="">Jour</option>
                {[
                  "Lundi",
                  "Mardi",
                  "Mercredi",
                  "Jeudi",
                  "Vendredi",
                  "Samedi",
                  "Dimanche",
                ].map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
              <input
                type="time"
                value={horaire.debut}
                onChange={(e) =>
                  handleHoraireChange(index, "debut", e.target.value)
                }
                className="input input-bordered"
              />
              <input
                type="time"
                value={horaire.fin}
                onChange={(e) =>
                  handleHoraireChange(index, "fin", e.target.value)
                }
                className="input input-bordered"
              />
            </div>
          ))}
          {horaires.length < 6 && (
            <button
              type="button"
              onClick={addHoraire}
              className="btn btn-sm mt-1 bg-primary text-white"
            >
              + Ajouter un horaire
            </button>
          )}

          {/* 👶 Sélection années / âge minimum */}
          <label className="font-semibold mt-4">
            Années de naissance ou âge minimum
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="modeAge"
                value="annees"
                checked={modeAge === "annees"}
                onChange={() => setModeAge("annees")}
              />
              Années
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="modeAge"
                value="ageMin"
                checked={modeAge === "ageMin"}
                onChange={() => setModeAge("ageMin")}
              />
              Âge minimum
            </label>
          </div>

          {modeAge === "annees" && (
            <div className="flex flex-wrap gap-2">
              {Array.from(
                { length: 20 },
                (_, i) => new Date().getFullYear() - i
              ).map((year) => (
                <label key={year} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={anneesSelectionnees.includes(year)}
                    onChange={(e) => {
                      setAnneesSelectionnees((prev) =>
                        e.target.checked
                          ? [...prev, year]
                          : prev.filter((y) => y !== year)
                      );
                    }}
                  />
                  {year}
                </label>
              ))}
            </div>
          )}

          {modeAge === "ageMin" && (
            <input
              type="number"
              className="input input-bordered w-40"
              placeholder="Âge minimum"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
            />
          )}

          <input
            type="number"
            name="tarif_licence"
            value={formData.tarif_licence}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Tarif de la licence"
          />
          <input
            type="number"
            name="tarif_cotisation"
            value={formData.tarif_cotisation}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Tarif de la cotisation"
          />
          {/* 📝 Info groupe (Quill) */}
          <label className="label-text font-semibold">
            Infos complémentaires
          </label>
          <ReactQuill
            value={infoGroupe}
            onChange={setInfoGroupe}
            theme="snow"
          />
          <input
            type="number"
            name="quota_groupe"
            value={formData.quota_groupe}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Quota du groupe"
          />
          <input
            type="number"
            name="ordre_groupe"
            value={formData.ordre_groupe}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Ordre du groupe"
          />
          {/* 🧑‍🏫 Enseignants dynamiques */}
          <label className="font-semibold">Enseignants</label>
          {enseignantsList.map((value, index) => (
            <select
              key={index}
              value={value}
              onChange={(e) => handleEnseignantChange(index, e.target.value)}
              className="input input-bordered w-full"
            >
              <option value="">Sélectionner Enseignant {index + 1}</option>
              {enseignants.map((e) => (
                <option key={e.id} value={`${e.nom} ${e.prenom}`}>
                  {e.nom} {e.prenom}
                </option>
              ))}
            </select>
          ))}
          {enseignantsList.length < 6 && (
            <button
              type="button"
              onClick={addEnseignant}
              className="btn btn-sm mt-1 bg-primary text-white"
            >
              + Ajouter un enseignant
            </button>
          )}
          <label className="flex items-center space-x-2">
            <span className="label-text">Groupe actif ?</span>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="toggle toggle-primary"
            />
          </label>
          <label className="flex flex-col space-y-2">
            <span className="label-text">Couleur du groupe</span>
            <div className="flex flex-wrap gap-2">
              {colors.map((color) => (
                <div
                  key={color.value}
                  className={`w-8 h-8 rounded-md cursor-pointer border-2 transition ${
                    color.value
                  } 
          ${
            formData.color_groupe === color.value
              ? "border-black scale-110"
              : "border-gray-300"
          }`}
                  onClick={() => handleColorChange(color)}
                ></div>
              ))}
            </div>
          </label>
          <div className="flex justify-center space-x-2 mt-4">
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="btn-ghost gap-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGroupModal;
